import { error, success } from "@redux-requests/core";
import { getSelectableIds } from "utils/helpers";

const initialState = {
	originalData: {},
	allFilterOptions: {},
	selectedFilterOptions: {},
	currentData: {},
	savedAnalyticFilters: {},
	loading: false,
};

const getReportsData = ({ cjms, personas, touchpoints, opportunities, solutions, actions, insights, tags, groups, gainInsightTypeId, painInsightTypeId }) => {
	if (cjms.length === 0 && personas.length === 0)
		return {
			touchpoints: [],
			opportunities: [],
			solutions: [],
			actions: [],
			insights: [],
			gainInsights: {},
			painInsights: {},
			tags: [],
			groups: [],
		};

	const currentCjmIds = cjms.map((cjm) => cjm.id);
	const currentPersonaIds = personas.map((p) => p.id);

	const filterRecords = ({ records, mode = "and", single = false }) => {
		return records.filter((record) => {
			const hasJourney = single ? currentCjmIds.includes(record?.cjmId) : record?.cjmIds?.some((cjmId) => currentCjmIds.includes(cjmId));
			const hasPersona = single ? currentPersonaIds.includes(record?.personaId) : record?.personaIds?.some((personaId) => currentPersonaIds.includes(personaId));
			return mode === "and" ? hasPersona && hasJourney : hasPersona || hasJourney;
		});
	};

	const gainInsights = {};
	const painInsights = {};
	const filteredInsights = filterRecords({ records: insights });

	cjms.forEach((cjm) => {
		cjm.insightIds?.forEach((insightId) => {
			const insight = filteredInsights.find((currInsight) => currInsight.id === insightId);
			if (insight) {
				if (insight?.type?.id === gainInsightTypeId) {
					if (!(insightId in gainInsights)) gainInsights[insightId] = { name: insight?.name, value: 0, cjms: [], wordCloud: insight.wordCloud };
					gainInsights[insightId] = { ...gainInsights[insightId], value: gainInsights[insightId].value + 1, cjms: [...gainInsights[insightId].cjms, cjm] };
				}
				if (insight?.type?.id === painInsightTypeId) {
					if (!(insightId in painInsights)) painInsights[insightId] = { name: insight?.name, value: 0, cjms: [], wordCloud: insight.wordCloud };
					painInsights[insightId] = { ...painInsights[insightId], value: painInsights[insightId].value + 1, cjms: [...painInsights[insightId].cjms, cjm] };
				}
			}
		});
	});

	return {
		touchpoints: filterRecords({ records: touchpoints, single: true }),
		opportunities: filterRecords({ records: opportunities }),
		solutions: filterRecords({ records: solutions }),
		actions: filterRecords({ records: actions }),
		insights: filteredInsights,
		gainInsights,
		painInsights,
		tags: filterRecords({ records: tags, mode: "or" }),
		groups: groups?.filter((group) => group?.cjmIds.some((cjmId) => currentCjmIds.includes(cjmId))),
	};
};

const journeyTrendReducer = (state = initialState, action) => {
	const responseData = action?.response?.data;

	switch (action.type) {
		case success("GET_JOURNEY_TREND_DATA"): {
			const { isExportMode } = action?.meta?.requestAction?.params;
			const selectedCjms = responseData?.cjms?.filter((j) => state?.savedAnalyticFilters?.cjms?.includes(j.id));
			const selectedPersonas = responseData?.personas?.filter((p) => state?.savedAnalyticFilters?.personas?.includes(p.id));

			if (isExportMode) {
				return {
					...state,
					originalData: responseData,
					selectedFilterOptions: {
						programs: responseData?.programs?.filter((p) => state?.savedAnalyticFilters?.programs?.includes(p.id)),
						cjms: selectedCjms,
						personas: selectedPersonas,
					},
					currentData: getReportsData({ ...responseData, cjms: selectedCjms, personas: selectedPersonas }),
					loading: false,
				};
			}
			return {
				...state,
				originalData: responseData,
				allFilterOptions: { programs: responseData.programs, cjms: responseData.cjms, personas: responseData.personas },
				selectedFilterOptions: { programs: [], cjms: responseData.cjms, personas: responseData.personas },
				currentData: getReportsData(responseData),
				loading: false,
			};
		}

		case error("GET_JOURNEY_TREND_DATA"):
			return state;
		case "APPLY_FILTER_JOURNEY_TRENDS": {
			const { dataAttribute, selectedItems } = action?.payload;

			switch (dataAttribute) {
				case "programs": {
					if (selectedItems.length === 0) {
						return {
							...state,
							allFilterOptions: { programs: state.originalData.programs, cjms: state.originalData.cjms, personas: state.originalData.personas },
							selectedFilterOptions: { programs: [], cjms: state.originalData.cjms, personas: state.originalData.personas },
							currentData: getReportsData(state.originalData),
						};
					}

					const selectableCjmIds = getSelectableIds({
						selectedItems,
						linkedAttribute: "cjmIds",
					});
					const filteredCjms = state.originalData.cjms?.filter((cjm) => selectableCjmIds.includes(cjm.id));

					const selectablePersonaIds = getSelectableIds({
						selectedItems: filteredCjms,
						linkedAttribute: "personaIds",
					});

					const filteredPersonas = state.originalData.personas?.filter((persona) => selectablePersonaIds.includes(persona.id));
					return {
						...state,
						allFilterOptions: { ...state.allFilterOptions, cjms: filteredCjms, personas: filteredPersonas },
						selectedFilterOptions: { programs: selectedItems, cjms: filteredCjms, personas: filteredPersonas },
						currentData: getReportsData({ ...state.originalData, cjms: filteredCjms, personas: filteredPersonas }),
						loading: false,
					};
				}
				case "cjms": {
					if (selectedItems.length === 0) {
						return {
							...state,
							allFilterOptions: { ...state.allFilterOptions, personas: [] },
							selectedFilterOptions: { ...state.selectedFilterOptions, cjms: [], personas: [] },
							currentData: getReportsData({ cjms: [], personas: [] }),
						};
					}

					const selectablePersonaIds = getSelectableIds({
						selectedItems,
						linkedAttribute: "personaIds",
					});

					const filteredPersonas = state.originalData.personas?.filter((persona) => selectablePersonaIds.includes(persona.id));

					return {
						...state,
						allFilterOptions: { ...state.allFilterOptions, personas: filteredPersonas },
						selectedFilterOptions: { ...state.selectedFilterOptions, cjms: selectedItems, personas: filteredPersonas },
						currentData: getReportsData({ ...state.originalData, cjms: selectedItems, personas: filteredPersonas }),
						loading: false,
					};
				}
				case "personas": {
					return {
						...state,
						selectedFilterOptions: { ...state.selectedFilterOptions, personas: selectedItems },
						currentData: getReportsData({ ...state.originalData, personas: selectedItems }),
						loading: false,
					};
				}
				case "tags": {
					if (selectedItems.length === 0) {
						return {
							...state,
							selectedFilterOptions: { ...state.selectedFilterOptions, tags: [] },
							currentData: getReportsData({ ...state.originalData, cjms: state?.selectedFilterOptions?.cjms, personas: state.selectedFilterOptions.personas }),
						};
					}
					const allRelatedCjmIds = [];
					const allRelatedPersonaIds = [];
					selectedItems.forEach((tag) => {
						allRelatedCjmIds.push(...tag.cjmIds);
						allRelatedPersonaIds.push(...tag.personaIds);
					});
					const selectedCjms = state?.selectedFilterOptions?.cjms?.filter((cjm) => allRelatedCjmIds?.includes(cjm?.id));
					const selectedPersonas = state.selectedFilterOptions.personas?.filter((persona) => allRelatedPersonaIds?.includes(persona?.id));
					return {
						...state,
						selectedFilterOptions: { ...state.selectedFilterOptions, tags: selectedItems },
						currentData: getReportsData({ ...state.originalData, cjms: selectedCjms, personas: selectedPersonas }),
						loading: false,
					};
				}
				default:
					return state;
			}
		}
		case success("GET_ANALYTICS_FILTERS"): {
			return {
				...state,
				savedAnalyticFilters: responseData,
			};
		}

		default:
			return state;
	}
};
export default journeyTrendReducer;
